/*global analytics navigator*/
import Bluebird from 'bluebird';
import autobind from 'autobind-decorator';
import {withConfig} from 'spa/Components/Context';
import {observe} from 'mobx';

import uuidv4 from 'uuid/v4';
import Cookies from 'js-cookie';

const TIMEOUT = 1500;
const log = process.env.NODE_ENV === 'development' ? (...args) => console.log(...args) : () => {}; // eslint-disable-line no-console,max-len

function timeout(target, prop, descriptor) {
  if (typeof descriptor.value !== 'function') throw new Error('Invalid use of @timeout');

  const {value: fn} = descriptor;

  return {
    ...descriptor,
    value(...args) {
      return Bluebird.race([
        fn.apply(this, args),
        Bluebird.delay(TIMEOUT),
      ]);
    },
  };
}

class Tracker {
  @autobind
  @timeout
  async track(eventName, eventData) {
    const {umDeviceIdentifier} = this.ctx;
    const {
      bookingFlowID,
      sessionID,
      slotSearchID,
      workersSearchID,
    } = this;

    log('tracker#track', eventName, eventData);

    await this.ready();

    if (typeof analytics === 'undefined') return;

    const localCounter = this.sequenceID;

    this.sequenceID += 1;

    const evData = {
      layer: 'web',
      ...eventData,
      bookingFlowID,
      sequenceID: localCounter,
      hostname: document.location.hostname,
      path: document.location.pathname,
      sessionID,
      slotSearchID,
      umDeviceIdentifier,
      workersSearchID,
    };

    const traits = (analytics && typeof analytics.user === 'function') ?
      analytics.user().traits() :
      {};

    await new Bluebird(resolve =>
      analytics.track(eventName, evData, {traits}, resolve));
  }

  @autobind
  @timeout
  async page(properties) {
    log('tracker#page');
    await this.ready();
    if (typeof analytics === 'undefined') return;

    if (properties) {
      properties.sequenceID = this.sequenceID;
      properties.umDeviceIdentifier = this.ctx.umDeviceIdentifier;
      properties.sessionID = this.sessionID;
      properties.slotSearchID = this.slotSearchID;
      properties.workersSearchID = this.workersSearchID;
    }

    this.sequenceID += 1;

    const traits = (analytics && typeof analytics.user === 'function') ?
      analytics.user().traits() :
      {};

    await new Bluebird(resolve =>
      analytics.page(properties, {traits}, resolve));
  }

  @autobind
  @timeout
  async identify(user) {
    // We need to do this here, in case the first hit is a non server side rendered page
    let identifierCookie = Cookies.get('umDeviceIdentifier');

    // If there isn't a cookie with the uuid for umDeviceIdentifier, we then need to create one
    if (!identifierCookie) {
      identifierCookie = uuidv4();

      Cookies.set('umDeviceIdentifier', identifierCookie);
    }

    this.ctx.umDeviceIdentifier = identifierCookie;

    log('tracker#identify', user);
    await this.ready();
    if (typeof analytics === 'undefined') return;

    let anonymousId = Cookies.get('segment-anonymous-id');

    if (!anonymousId && analytics && typeof analytics.user === 'function') {
      const generatedAnonymousId = analytics.user().anonymousId();

      Cookies.set('segment-anonymous-id', generatedAnonymousId);

      anonymousId = generatedAnonymousId;
    }

    this.ctx.anonymousId = anonymousId;

    if (user) {
      analytics.identify(user.id, {
        name: user.name,
        email: user.email,
        umDeviceIdentifier: this.ctx.umDeviceIdentifier,
      }, {
        anonymousId,
      });
    } else {
      analytics.reset();
      analytics.identify(null, {
        umDeviceIdentifier: this.ctx.umDeviceIdentifier,
      }, {
        anonymousId,
      });
    }
  }

  @autobind
  @timeout
  async ready() {
    if (typeof analytics === 'undefined') return;
    await new Bluebird(resolve =>
      analytics.ready(resolve));
    log('tracker#ready');
  }

  @autobind
  setBookingFlowID() {
    this.bookingFlowID = uuidv4();
  }

  @autobind
  resetBookingFlowID() {
    this.bookingFlowID = undefined;
  }

  @autobind
  resetWorkersSearchID() {
    this.workersSearchID = undefined;
  }

  @autobind
  resetSlotSearchID() {
    this.slotSearchID = undefined;
  }

  trackCriteoEvent(event, region) {
    const {config, i18n} = this.ctx;
    if (typeof window !== 'undefined') {
      let deviceType = 'd';

      if (/iPad/.test(navigator.userAgent)) {
        deviceType = 't';
      } else if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Silk/.test(navigator.userAgent)) {
        deviceType = 'm';
      }

      if (!window.criteo_q) {
        window.criteo_q = [];
      }
      window.criteo_q.push({
        event: 'setAccount',
        account: config.getValue('tracking.criteo-account-id', i18n.umRegion),
      }, {
        event: 'setSiteType',
        type: deviceType,
      });
      if (this.ctx.session.user) {
        window.criteo_q.push({
          event: 'setEmail',
          email: this.ctx.session.user.email,
        });
      }

      setTimeout(() => {
        window.criteo_q.push(event);
      }, 100);
    }
  }

  constructor(ctx) {
    Object.defineProperty(this, 'ctx', {
      value: ctx,
      enumerable: false,
    });

    this.sequenceID = 0;

    if (typeof window !== 'undefined') {
      this.sessionID = window.sessionStorage.getItem('umSessionID');

      if (!this.sessionID) {
        this.sessionID = uuidv4();
        window.sessionStorage.setItem('umSessionID', this.sessionID);
      }

      this.slotSearchID;
      this.workersSearchID;

      this.page({});
    }
  }
}

export default Tracker;



// WEBPACK FOOTER //
// ./spa/state/tracker.jsx